/**
 * Adapted from PlayCanvas Engine's framework/parsers/sog-bundle.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type {
  GSplatHandler,
  GSplatLoadUrlObject,
  GSplatParserContext,
  IGSplatParser,
} from '@/plugins/gsplat/loaders/GSplatHandler'
import {
  buildSogResource,
  type SogParserOptions,
  type SogResource,
} from '@/plugins/gsplat/loaders/SogParser'

const EOCD_SIGNATURE = 0x06054b50
const CDR_SIGNATURE = 0x02014b50
const LFH_SIGNATURE = 0x04034b50
const EOCD_LENGTH = 22

export interface SogZipEntry {
  readonly filename: string
  readonly compression: 'none' | 'deflate'
  readonly compressedSize: number
  readonly uncompressedSize: number
  readonly data: Uint8Array
}

export interface SogBundleParserOptions extends SogParserOptions {}

/** 冻结 PlayCanvas EOCD/CDR/LFH bundle 协议，并在切 view 前完成所有 bounds 检查。 */
export function parseSogZipArchive(buffer: ArrayBuffer): readonly SogZipEntry[] {
  if (buffer.byteLength < EOCD_LENGTH) throw new Error('SogBundleParser: EOCDR not found')
  const view = new DataView(buffer)
  const eocdOffset = buffer.byteLength - EOCD_LENGTH
  if (view.getUint32(eocdOffset, true) !== EOCD_SIGNATURE) {
    throw new Error('SogBundleParser: EOCDR not found')
  }
  const disk = view.getUint16(eocdOffset + 4, true)
  const centralDisk = view.getUint16(eocdOffset + 6, true)
  const diskFiles = view.getUint16(eocdOffset + 8, true)
  const numFiles = view.getUint16(eocdOffset + 10, true)
  const centralSize = view.getUint32(eocdOffset + 12, true)
  const centralOffset = view.getUint32(eocdOffset + 16, true)
  const commentLength = view.getUint16(eocdOffset + 20, true)
  if (commentLength !== 0) throw new Error('SogBundleParser: ZIP comment 不受冻结协议支持')
  if (
    disk !== 0 ||
    centralDisk !== 0 ||
    diskFiles !== numFiles ||
    numFiles === 0xffff ||
    centralSize === 0xffffffff ||
    centralOffset === 0xffffffff
  ) {
    throw new Error('SogBundleParser: multi-disk/Zip64 不受支持')
  }
  assertRange(buffer.byteLength, centralOffset, centralSize, 'central directory')

  const decoder = new TextDecoder()
  const entries: SogZipEntry[] = []
  const filenames = new Set<string>()
  let offset = centralOffset
  for (let index = 0; index < numFiles; index++) {
    assertRange(buffer.byteLength, offset, 46, 'CDR')
    if (view.getUint32(offset, true) !== CDR_SIGNATURE) {
      throw new Error('SogBundleParser: CDR not found')
    }
    const flags = view.getUint16(offset + 8, true)
    const method = view.getUint16(offset + 10, true)
    const compressedSize = view.getUint32(offset + 20, true)
    const uncompressedSize = view.getUint32(offset + 24, true)
    const filenameLength = view.getUint16(offset + 28, true)
    const extraLength = view.getUint16(offset + 30, true)
    const commentLength = view.getUint16(offset + 32, true)
    const localOffset = view.getUint32(offset + 42, true)
    if (
      compressedSize === 0xffffffff ||
      uncompressedSize === 0xffffffff ||
      localOffset === 0xffffffff
    ) {
      throw new Error('SogBundleParser: Zip64 不受支持')
    }
    if ((flags & 1) !== 0) throw new Error('SogBundleParser: encrypted entry 不受支持')
    const compression = ({ 0: 'none', 8: 'deflate' } as const)[method as 0 | 8]
    if (!compression) throw new Error(`SogBundleParser: compression method ${method} 不受支持`)
    const recordLength = 46 + filenameLength + extraLength + commentLength
    assertRange(buffer.byteLength, offset, recordLength, 'CDR record')
    const filename = decoder.decode(new Uint8Array(buffer, offset + 46, filenameLength))
    if (!filename || filenames.has(filename)) {
      throw new Error(`SogBundleParser: duplicate/empty filename "${filename}"`)
    }
    filenames.add(filename)

    assertRange(buffer.byteLength, localOffset, 30, 'LFH')
    if (view.getUint32(localOffset, true) !== LFH_SIGNATURE) {
      throw new Error('SogBundleParser: LFH not found')
    }
    const localFilenameLength = view.getUint16(localOffset + 26, true)
    const localExtraLength = view.getUint16(localOffset + 28, true)
    const dataOffset = localOffset + 30 + localFilenameLength + localExtraLength
    assertRange(buffer.byteLength, dataOffset, compressedSize, `entry ${filename}`)
    entries.push({
      filename,
      compression,
      compressedSize,
      uncompressedSize,
      data: new Uint8Array(buffer, dataOffset, compressedSize),
    })
    offset += recordLength
  }
  if (offset !== centralOffset + centralSize) {
    throw new Error('SogBundleParser: central directory size 不匹配')
  }
  return Object.freeze(entries.map((entry) => Object.freeze(entry)))
}

export class SogBundleParser implements IGSplatParser<SogResource, SogBundleParserOptions> {
  handler?: GSplatHandler<SogResource, SogBundleParserOptions> | null

  canParse(context: GSplatParserContext<SogBundleParserOptions>): boolean {
    return context.ext === 'sog'
  }

  async load(
    url: GSplatLoadUrlObject,
    context: GSplatParserContext<SogBundleParserOptions>
  ): Promise<SogResource> {
    const options = context.options
    if (!options?.adapter) throw new Error('SogBundleParser: 缺少 GSplatGraphicsAdapter')
    const maxRetries = this.handler?.maxRetries ?? 0
    const archive = await downloadArrayBuffer(url.load, options, context.signal, maxRetries)
    throwIfAborted(context.signal)
    const entries = parseSogZipArchive(archive)
    const files = new Map(entries.map((entry) => [entry.filename, entry]))
    const metaEntry = files.get('meta.json')
    if (!metaEntry) throw new Error('SogBundleParser: meta.json not found')
    const metaBytes = await extractEntry(metaEntry, context.signal)
    let meta: unknown
    try {
      meta = JSON.parse(new TextDecoder().decode(metaBytes))
    } catch (error) {
      throw new Error(`SogBundleParser: meta.json 解析失败 [${String(error)}]`)
    }

    return buildSogResource({
      meta,
      url: url.original,
      options,
      signal: context.signal,
      resolveTexture: async (filename, signal) => {
        const embedded = files.get(filename)
        if (embedded) return new Blob([toArrayBuffer(await extractEntry(embedded, signal))])
        const mapped = options.mapUrl?.(filename) ?? resolveExternalUrl(filename, url)
        return fetchBlob(mapped, options, signal, maxRetries)
      },
    })
  }
}

async function extractEntry(entry: SogZipEntry, signal?: AbortSignal): Promise<Uint8Array> {
  throwIfAborted(signal)
  let result: Uint8Array
  if (entry.compression === 'none') {
    result = entry.data.slice()
  } else {
    if (typeof DecompressionStream === 'undefined') {
      throw new Error('SogBundleParser: 当前环境不支持 DecompressionStream')
    }
    const compressed = entry.data.slice()
    const input = new ReadableStream<BufferSource>({
      start(controller) {
        controller.enqueue(compressed)
        controller.close()
      },
    })
    const output = input.pipeThrough(new DecompressionStream('deflate-raw'))
    result = new Uint8Array(await new Response(output).arrayBuffer())
  }
  throwIfAborted(signal)
  if (result.byteLength !== entry.uncompressedSize) {
    throw new Error(
      `SogBundleParser: ${entry.filename} 解压长度不匹配，expected=${entry.uncompressedSize}, actual=${result.byteLength}`
    )
  }
  return result
}

async function downloadArrayBuffer(
  source: string | Blob,
  options: SogBundleParserOptions,
  signal?: AbortSignal,
  maxRetries = 0
): Promise<ArrayBuffer> {
  throwIfAborted(signal)
  if (source instanceof Blob) {
    const result = await source.arrayBuffer()
    options.progress?.(result.byteLength, result.byteLength)
    return result
  }
  const response = await fetchWithRetries(source, requestInit(options, signal), maxRetries, signal)
  if (!response.ok) {
    throw new Error(`SogBundleParser: bundle 加载失败 ${response.status} ${response.statusText}`)
  }
  const total = Number.parseInt(response.headers.get('content-length') ?? '0', 10)
  if (!response.body) {
    const result = await response.arrayBuffer()
    options.progress?.(result.byteLength, total || result.byteLength)
    return result
  }
  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let loaded = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      loaded += value.byteLength
      options.progress?.(loaded, total)
      throwIfAborted(signal)
    }
  } finally {
    reader.releaseLock()
  }
  return joinBytes(chunks)
}

async function fetchBlob(
  url: string,
  options: SogBundleParserOptions,
  signal?: AbortSignal,
  maxRetries = 0
): Promise<Blob> {
  throwIfAborted(signal)
  const response = await fetchWithRetries(url, requestInit(options, signal), maxRetries, signal)
  if (!response.ok) throw new Error(`SogBundleParser: external texture 加载失败 ${response.status}`)
  const result = await response.blob()
  throwIfAborted(signal)
  return result
}

function requestInit(
  options: SogBundleParserOptions,
  signal?: AbortSignal
): RequestInit | undefined {
  if (!options.withCredentials && !signal) return undefined
  return {
    ...(options.withCredentials ? { credentials: 'include' as const } : {}),
    ...(signal ? { signal } : {}),
  }
}

async function fetchWithRetries(
  url: string,
  init: RequestInit | undefined,
  maxRetries: number,
  signal?: AbortSignal
): Promise<Response> {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    throwIfAborted(signal)
    try {
      const response = await fetch(url, init)
      if (response.ok || attempt === maxRetries) return response
      await response.body?.cancel().catch(() => undefined)
    } catch (error) {
      lastError = error
      if (signal?.aborted || attempt === maxRetries) throw error
    }
  }
  throw lastError ?? new Error(`SogBundleParser: fetch 失败 ${url}`)
}

function resolveExternalUrl(filename: string, url: GSplatLoadUrlObject): string {
  const parent = typeof url.load === 'string' ? url.load : url.original
  try {
    const documentBase = globalThis.document?.baseURI ?? globalThis.location?.href
    const base = documentBase ? new URL(parent, documentBase) : new URL(parent)
    return new URL(filename, base).toString()
  } catch {
    return filename
  }
}

function assertRange(total: number, offset: number, length: number, label: string): void {
  if (
    !Number.isSafeInteger(offset) ||
    !Number.isSafeInteger(length) ||
    offset < 0 ||
    length < 0 ||
    offset > total ||
    length > total - offset
  ) {
    throw new Error(`SogBundleParser: ${label} 越界`)
  }
}

function toArrayBuffer(value: Uint8Array): ArrayBuffer {
  return new Uint8Array(value.buffer, value.byteOffset, value.byteLength).slice().buffer
}

function joinBytes(chunks: readonly Uint8Array[]): ArrayBuffer {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0)
  const result = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.byteLength
  }
  return result.buffer
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('SogBundleParser load 已取消', 'AbortError')
}
